/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Button, Stack, Text, Flex} from '@salesforce/retail-react-app/app/components/shared/ui'
import LoginFields from '../../components/forms/login-fields'
import SocialLogin from '../../components/social-login'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import GoogleRecatpchaText from '../google-recatpcha-text'

const StandardLogin = ({
    form,
    handleForgotPasswordClick,
    hideEmail = false,
    setShowPasswordView,
    idps = [],
    shouldShowSocial,
    enabledIDPs,
    isLoginPage,
    prefix = ''
}) => {
    return (
        <Stack spacing={8}>
            <Stack>
                <LoginFields
                    form={form}
                    hideEmail={hideEmail}
                    handleForgotPasswordClick={handleForgotPasswordClick}
                    prefix={prefix}
                    isLoginPage={isLoginPage}
                />
            </Stack>
            <Stack spacing={3.5}>
                <Button
                    variant="webPrimary"
                    type="submit"
                    data-testid="login-sign-in-button"
                    onClick={() => {
                        form.clearErrors('global')
                    }}
                    isLoading={form.formState.isSubmitting}
                    _focusVisible={{
                        outline: '2px solid',
                        outlineColor: 'webTheme.focus',
                        outlineOffset: '2px'
                    }}
                >
                    <FormattedMessage defaultMessage="Sign In" id="login_form.button.sign_in" />
                </Button>
                <GoogleRecatpchaText
                    isStandardLogin={true}
                    enabledIDPs={enabledIDPs}
                    isLoginPage={isLoginPage}
                />
                {shouldShowSocial && idps.length > 0 && (
                    <>
                        <Stack spacing={6} marginTop="10px">
                            <Text align="start" fontSize="sm" fontWeight="sm" lineHeight="18px">
                                <FormattedMessage
                                    defaultMessage="Or Sign In With"
                                    id="contact_info.message.or_sign_in_with"
                                />
                            </Text>
                        </Stack>
                        <Flex justifyContent="center" gap="30px">
                            <SocialLogin form={form} idps={idps} enabledIDPs={enabledIDPs} />
                        </Flex>
                    </>
                )}
                {hideEmail && (
                    <Button
                        onClick={() => setShowPasswordView(false)}
                        borderColor="gray.500"
                        color="blue.600"
                        variant="outline"
                    >
                        <FormattedMessage
                            defaultMessage="Back to Sign In Options"
                            id="login_form.button.back"
                        />
                    </Button>
                )}
            </Stack>
        </Stack>
    )
}

StandardLogin.propTypes = {
    form: PropTypes.object,
    handleForgotPasswordClick: PropTypes.func,
    hideEmail: PropTypes.bool,
    isSocialEnabled: PropTypes.bool,
    setShowPasswordView: PropTypes.func,
    idps: PropTypes.arrayOf(PropTypes.string),
    shouldShowSocial: PropTypes.bool,
    enabledIDPs: PropTypes.arrayOf(PropTypes.string),
    isLoginPage: PropTypes.bool,
    prefix: PropTypes.string
}

export default StandardLogin
